import { useEffect, useState, useCallback } from "react";
import { api, apiError, fmtINR } from "../../lib/api";
import { PageHeader } from "../../components/Layout";
import { StatusBadge } from "../../components/StatusBadge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { Star, XCircle, Receipt, Package, Calculator } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { openRazorpayCheckout } from "../../lib/razorpay";

const StarPick = ({ value, onChange, testid }) => (
  <div className="flex gap-1" data-testid={testid}>
    {[1, 2, 3, 4, 5].map((i) => (
      <button key={i} type="button" onClick={() => onChange(i)} data-testid={`${testid}-${i}`} className="transition-transform hover:scale-110">
        <Star size={26} weight={i <= value ? "fill" : "regular"} className={i <= value ? "text-amber-500" : "text-muted-foreground/40"} />
      </button>
    ))}
  </div>
);

export default function CustomerRequests() {
  const [rows, setRows] = useState([]);
  const [fbMap, setFbMap] = useState({});
  const [rateJob, setRateJob] = useState(null);
  const [nps, setNps] = useState(null);
  const [service, setService] = useState(0);
  const [partnerRating, setPartnerRating] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [cancelJob, setCancelJob] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelBusy, setCancelBusy] = useState(false);
  const [addonJob, setAddonJob] = useState(null);
  const [payBusy, setPayBusy] = useState(false);
  const navigate = useNavigate();

  const CANCELLABLE = ["requested", "assigned", "accepted"];

  const load = useCallback(() => {
    api.get("/customer/requests").then((r) => {
      setRows(r.data);
      setAddonJob((cur) => cur ? r.data.find((x) => x.id === cur.id) || null : null);
    });
    api.get("/customer/feedback").then((r) => setFbMap(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const decideAddon = async (jid, addonId, accept) => {
    try {
      await api.post(`/customer/jobs/${jid}/addons/${addonId}/decide`, { accept });
      toast.success(accept ? "Item approved" : "Item declined");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const payBalance = async (job) => {
    setPayBusy(true);
    try {
      const { data: order } = await api.post(`/customer/jobs/${job.id}/pay-balance/create-order`);
      const pay = await openRazorpayCheckout(order, { description: "Fixigo — pay balance" });
      await api.post(`/customer/jobs/${job.id}/pay-balance/verify`, {
        razorpay_order_id: order.razorpay_order_id, razorpay_payment_id: pay.razorpay_payment_id,
        razorpay_signature: pay.razorpay_signature });
      toast.success("Payment successful! Balance cleared.");
      load();
    } catch (e) {
      if (e?.message === "Payment cancelled") toast.error("Payment cancelled");
      else toast.error(apiError(e.response?.data?.detail) || e.message);
    } finally { setPayBusy(false); }
  };

  const doCancel = async () => {
    if (!cancelReason.trim()) { toast.error("Please tell us why you're cancelling"); return; }
    setCancelBusy(true);
    try {
      await api.post(`/customer/jobs/${cancelJob.id}/cancel`, { reason: cancelReason.trim() });
      toast.success("Booking cancelled");
      setCancelJob(null); setCancelReason("");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setCancelBusy(false); }
  };

  const openRate = (j) => {
    setRateJob(j); setNps(null); setService(0); setPartnerRating(0); setComment("");
  };

  const submit = async () => {
    if (nps === null) { toast.error("Please pick a recommendation score (0-10)"); return; }
    if (!service) { toast.error("Please rate the service"); return; }
    setBusy(true);
    try {
      await api.post(`/customer/jobs/${rateJob.id}/feedback`, {
        nps_score: nps, service_rating: service, partner_rating: partnerRating, comment,
      });
      toast.success("Thanks for your feedback!");
      setRateJob(null);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <PageHeader title="My Requests" subtitle="Track the status of your services"
        actions={<Button className="rounded-sm" onClick={() => navigate("/account/new")} data-testid="new-request-btn">Book Service</Button>} />
      <div className="p-8">
        <div className="border border-border rounded-sm bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/50">
                <TableHead>Service</TableHead><TableHead>Partner</TableHead>
                <TableHead>Schedule</TableHead><TableHead>Address</TableHead>
                <TableHead className="text-right">Amount</TableHead><TableHead>Status</TableHead>
                <TableHead className="text-right">Feedback</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-testid="customer-requests-table">
              {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">No requests yet</TableCell></TableRow>}
              {rows.map((j) => (
                <TableRow key={j.id} className="hover:bg-secondary/30">
                  <TableCell>
                    <div className="font-semibold">{j.service_type}</div>
                    <div className="text-xs text-muted-foreground">{j.product_category} {j.product_type && `· ${j.product_type}`}</div>
                  </TableCell>
                  <TableCell>{j.partner_name || <span className="text-amber-600 text-xs">Awaiting assignment</span>}</TableCell>
                  <TableCell className="text-sm">{j.scheduled_date || "—"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground max-w-[180px] truncate">{j.address || "—"}</TableCell>
                  <TableCell className="text-right font-mono">{fmtINR(j.amount)}</TableCell>
                  <TableCell><StatusBadge value={j.status} /></TableCell>
                  <TableCell className="text-right">
                    <div className="flex flex-col items-end gap-1.5">
                      {(j.addons || []).some((a) => a.status === "pending") && (
                        <Button size="sm" variant="outline" className="rounded-sm gap-1.5 border-primary/40 text-primary" onClick={() => setAddonJob(j)} data-testid={`review-addons-${j.id}`}>
                          <Package size={14} /> Review Items ({(j.addons || []).filter((a) => a.status === "pending").length})
                        </Button>
                      )}
                      {j.balance_due > 0 && (
                        <Button size="sm" className="rounded-sm gap-1.5 bg-green-600 hover:bg-green-700" onClick={() => payBalance(j)} disabled={payBusy} data-testid={`pay-balance-${j.id}`}>
                          <Receipt size={14} /> Pay Balance {fmtINR(j.balance_due)}
                        </Button>
                      )}
                      {["requested", "assigned", "accepted", "in_progress"].includes(j.status) && (
                        <a href={`/estimate?id=${encodeURIComponent(j.b2b_partner_order_id || j.id)}`} target="_blank" rel="noreferrer" data-testid={`estimate-link-${j.id}`}>
                          <Button size="sm" variant="outline" className="rounded-sm gap-1.5">
                            <Calculator size={14} /> Estimate / Pay Spares
                          </Button>
                        </a>
                      )}
                      {j.status === "completed" ? (
                        fbMap[j.id] ? (
                          <span className="inline-flex items-center gap-1 text-xs text-green-700" data-testid={`rated-${j.id}`}>
                            <Star size={13} weight="fill" className="text-amber-500" /> Rated
                          </span>
                        ) : (
                          <Button size="sm" variant="outline" className="rounded-sm gap-1.5" onClick={() => openRate(j)} data-testid={`rate-${j.id}`}>
                            <Star size={14} /> Rate Service
                          </Button>
                        )
                      ) : CANCELLABLE.includes(j.status) ? (
                        <Button size="sm" variant="outline" className="rounded-sm gap-1.5 text-destructive border-destructive/30 hover:bg-destructive/5"
                          onClick={() => { setCancelJob(j); setCancelReason(""); }} data-testid={`cancel-${j.id}`}>
                          <XCircle size={14} /> Cancel
                        </Button>
                      ) : j.status === "cancelled" ? (
                        <span className="text-xs text-muted-foreground" data-testid={`cancelled-${j.id}`}>Cancelled</span>
                      ) : (!(j.addons || []).some((a) => a.status === "pending") && !(j.balance_due > 0)) ? <span className="text-xs text-muted-foreground">—</span> : null}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={!!rateJob} onOpenChange={(o) => !o && setRateJob(null)}>
        <DialogContent data-testid="rate-dialog">
          <DialogHeader><DialogTitle className="font-heading">Rate your service</DialogTitle></DialogHeader>
          {rateJob && (
            <div className="space-y-5 py-2">
              <div className="bg-secondary rounded-sm p-3 text-sm">
                <p className="font-semibold">{rateJob.service_type} · {rateJob.product_category}</p>
                <p className="text-xs text-muted-foreground">{rateJob.partner_name || "—"}</p>
              </div>
              <div>
                <Label className="label-caps">How likely are you to recommend us? (0–10)</Label>
                <div className="flex flex-wrap gap-1.5 mt-2" data-testid="nps-row">
                  {Array.from({ length: 11 }, (_, i) => i).map((n) => (
                    <button key={n} type="button" onClick={() => setNps(n)} data-testid={`nps-${n}`}
                      className={`w-9 h-9 rounded-sm text-sm font-semibold border transition-colors ${nps === n ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}>{n}</button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="label-caps">Service quality</Label>
                <div className="mt-2"><StarPick value={service} onChange={setService} testid="service-stars" /></div>
              </div>
              <div>
                <Label className="label-caps">Partner rating (optional)</Label>
                <div className="mt-2"><StarPick value={partnerRating} onChange={setPartnerRating} testid="partner-stars" /></div>
              </div>
              <div>
                <Label className="label-caps">Comments (optional)</Label>
                <Textarea value={comment} onChange={(e) => setComment(e.target.value)} className="rounded-sm mt-1.5" placeholder="Tell us about your experience" data-testid="feedback-comment" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setRateJob(null)}>Cancel</Button>
            <Button className="rounded-sm" onClick={submit} disabled={busy} data-testid="submit-feedback">{busy ? "Submitting…" : "Submit Feedback"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!cancelJob} onOpenChange={(o) => !o && setCancelJob(null)}>
        <DialogContent data-testid="cancel-dialog">
          <DialogHeader><DialogTitle className="font-heading">Cancel booking</DialogTitle></DialogHeader>
          {cancelJob && (
            <div className="space-y-4 py-2">
              <div className="bg-secondary rounded-sm p-3 text-sm">
                <p className="font-semibold">{cancelJob.service_type} · {cancelJob.product_category}</p>
                <p className="text-xs text-muted-foreground">{cancelJob.scheduled_date || "—"} {cancelJob.slot || ""}</p>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs text-amber-800">
                Free cancellation up to 2 hours before the scheduled service. A ₹99 fee may apply if cancelled within 2 hours, or a visit charge if the technician has already arrived. See the{" "}
                <a href="/customer-terms" target="_blank" rel="noreferrer" className="font-semibold underline">cancellation policy</a>.
              </div>
              <div>
                <Label className="label-caps">Reason for cancellation *</Label>
                <Textarea value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} className="rounded-sm mt-1.5"
                  placeholder="Let us know why you're cancelling" data-testid="cancel-reason" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setCancelJob(null)}>Keep booking</Button>
            <Button className="rounded-sm bg-destructive hover:bg-destructive/90" onClick={doCancel} disabled={cancelBusy} data-testid="confirm-cancel">
              {cancelBusy ? "Cancelling…" : "Cancel Booking"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!addonJob} onOpenChange={(o) => !o && setAddonJob(null)}>
        <DialogContent data-testid="customer-addon-dialog">
          <DialogHeader><DialogTitle className="font-heading">Additional items proposed</DialogTitle></DialogHeader>
          {addonJob && (
            <div className="space-y-3 py-2 max-h-[60vh] overflow-y-auto">
              <p className="text-sm text-muted-foreground">Your technician has proposed the following additional work for <strong>{addonJob.service_type}</strong>. Approve the items you'd like done — approved items are added to your balance.</p>
              {(addonJob.addons || []).map((a) => (
                <div key={a.id} className="border border-border rounded-sm p-3 flex items-center justify-between gap-3" data-testid={`caddon-${a.id}`}>
                  <div>
                    <p className="text-sm font-medium">{a.name}</p>
                    <p className="text-xs text-muted-foreground">Qty {a.qty} × {fmtINR(a.rate)}{a.gst ? ` + GST ${fmtINR(a.gst)}` : ""} = <strong>{fmtINR(a.total)}</strong></p>
                  </div>
                  {a.status === "pending" ? (
                    <div className="flex gap-1.5 shrink-0">
                      <Button size="sm" className="rounded-sm bg-green-600 hover:bg-green-700" onClick={() => decideAddon(addonJob.id, a.id, true)} data-testid={`caddon-accept-${a.id}`}>Approve</Button>
                      <Button size="sm" variant="outline" className="rounded-sm text-destructive border-destructive/30" onClick={() => decideAddon(addonJob.id, a.id, false)} data-testid={`caddon-reject-${a.id}`}>Decline</Button>
                    </div>
                  ) : (
                    <span className={`text-xs font-semibold shrink-0 ${a.status === "accepted" ? "text-green-600" : "text-destructive"}`}>{a.status === "accepted" ? "Approved" : "Declined"}</span>
                  )}
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setAddonJob(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
